import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const STARTERS = [
  "أنشئ 10 أسئلة عن التاريخ الإسلامي",
  "راجع هذه الجملة وصحح الأخطاء الإملائية",
  "اقترح 5 فئات جديدة مثيرة للاهتمام",
  "اكتشف أي الأسئلة قد تكون مكررة في هذه الفئات",
  "كيف أرفع مستوى صعوبة هذا السؤال؟",
  "اقترح صورة مناسبة لسؤال عن الرياضة",
];

export default function AiAssistantPanel({ token }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "مرحباً! أنا مساعد حُجّة الذكي 🤖\n\nأستطيع مساعدتك في:\n• توليد الأسئلة\n• مراجعة المحتوى\n• تحسين الصياغة\n• اقتراح الصور والأصوات\n• اكتشاف التكرار\n\nاكتب أمراً أو اختر من الاقتراحات أدناه." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");

    const history = messages.filter(m => m.role !== "system").map(m => ({
      role: m.role, content: m.content
    }));

    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/admin/ai/chat`,
        { message: msg, history },
        { headers }
      );
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const err = e.response?.data?.detail || "خطأ في الاتصال بالمساعد الذكي";
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${err}`, isError: true }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([{
      role: "assistant",
      content: "تم مسح المحادثة. كيف يمكنني مساعدتك؟"
    }]);
  };

  return (
    <div className="flex flex-col h-[80vh] max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-black">🤖 مساعد AI</h2>
          <p className="text-primary/50 text-sm mt-0.5">مساعد ذكي لإدارة محتوى حُجّة</p>
        </div>
        <button onClick={clearChat}
          className="text-sm text-primary/50 hover:text-primary bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg font-bold transition-all">
          مسح المحادثة
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-secondary font-black text-sm flex-shrink-0 ml-2 mt-1">
                🤖
              </div>
            )}
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-primary text-secondary rounded-tr-sm font-bold"
                  : m.isError
                  ? "bg-red-50 border border-red-200 text-red-600"
                  : "bg-white border border-primary/10 text-primary"
              }`}
            >
              {m.content}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-primary font-black text-sm flex-shrink-0 mr-2 mt-1">
                👤
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-secondary font-black text-sm flex-shrink-0 ml-2">
              🤖
            </div>
            <div className="bg-white border border-primary/10 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1 items-center">
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Starter prompts (only when just the welcome message) */}
      {messages.length === 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {STARTERS.map((s, i) => (
            <button key={i} onClick={() => sendMessage(s)}
              className="text-xs bg-secondary/10 text-primary border border-secondary/20 hover:border-secondary/50 hover:bg-secondary/20 rounded-full px-3 py-1.5 font-bold transition-all">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-3 items-end">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="اكتب أمراً أو سؤالاً... (Enter للإرسال)"
          rows={2}
          className="flex-1 border-2 border-primary/20 focus:border-primary rounded-2xl px-4 py-3 text-sm font-bold outline-none resize-none"
          style={{ fontFamily: "Cairo, sans-serif" }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          className="bg-primary text-secondary w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl hover:scale-105 transition-all disabled:opacity-40 disabled:scale-100 flex-shrink-0"
        >
          {loading ? <span className="text-base animate-spin">⏳</span> : "↑"}
        </button>
      </div>
      <div className="text-xs text-primary/30 mt-1.5 text-center">
        Enter للإرسال · Shift+Enter لسطر جديد
      </div>
    </div>
  );
}
